import { Download, ExternalLink } from 'lucide-react';
import { Drawer } from '@/shared/components/ui/Drawer';
import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/Badge';
import { canPreview, detectFileType, deriveFileName, isImage, isPdf } from '../constants';
import type { SalesDoc } from '../types';

interface SalesDocDrawerProps {
  doc: SalesDoc | null;
  canManage: boolean;
  deleting?: boolean;
  onClose: () => void;
  onEdit: (doc: SalesDoc) => void;
  onDelete: (doc: SalesDoc) => void;
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  if (value === undefined || value === null || value === '') return null;
  return (
    <div className="flex justify-between gap-4 border-b border-outline py-2 last:border-0">
      <span className="text-body-sm text-on-surface-variant">{label}</span>
      <span className="text-right text-data-md text-on-surface">{value}</span>
    </div>
  );
}

function fmtDate(iso?: string) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
}

export function SalesDocDrawer({ doc, canManage, deleting, onClose, onEdit, onDelete }: SalesDocDrawerProps) {
  const ft = detectFileType(doc?.url);
  const Icon = ft.icon;

  return (
    <Drawer open={!!doc} onClose={onClose} title={doc?.title ?? 'Document'}>
      {doc && (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`flex h-9 w-9 items-center justify-center rounded-full bg-surface-variant ${ft.tone === 'danger' ? 'text-danger' : 'text-on-surface-variant'}`}>
              <Icon className="h-5 w-5" />
            </span>
            <Badge tone={ft.tone}>{ft.label}</Badge>
          </div>

          {doc.description && <p className="whitespace-pre-wrap text-data-md text-on-surface">{doc.description}</p>}

          <div>
            <Row label="File" value={deriveFileName(doc.url) || '—'} />
            <Row label="Uploaded By" value={doc.creator_name} />
            <Row label="Uploaded" value={fmtDate(doc.created_at)} />
          </div>

          {/* Preview only for images / PDFs */}
          {doc.url && canPreview(doc.url) && (
            <div>
              <h3 className="mb-2 text-label-caps uppercase text-on-surface-variant">Preview</h3>
              {isImage(doc.url) ? (
                <a href={doc.url} target="_blank" rel="noreferrer">
                  <img src={doc.url} alt={doc.title} className="max-h-72 w-full rounded-card border border-outline object-contain" />
                </a>
              ) : isPdf(doc.url) ? (
                <iframe title={doc.title} src={doc.url} className="h-80 w-full rounded-card border border-outline" />
              ) : null}
            </div>
          )}

          {/* Actions */}
          {doc.url ? (
            <div className="flex gap-2">
              <a href={doc.url} target="_blank" rel="noreferrer" className="flex-1">
                <Button variant="outline" className="w-full"><ExternalLink className="h-4 w-4" /> Open</Button>
              </a>
              <a href={doc.url} download={deriveFileName(doc.url)}>
                <Button variant="outline"><Download className="h-4 w-4" /> Download</Button>
              </a>
            </div>
          ) : (
            <p className="text-body-sm text-on-surface-variant">No document link attached.</p>
          )}

          {canManage && (
            <div className="flex gap-2 border-t border-outline pt-3">
              <Button onClick={() => onEdit(doc)} className="flex-1">Edit</Button>
              <Button variant="danger" loading={deleting} onClick={() => onDelete(doc)}>Delete</Button>
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
}
